import axios from "axios";
import csvtojson from "csvtojson";
import dayjs from "dayjs";
import { find, noop, omit } from "lodash";
import { makeCancellableAPIRequest } from "restAPI";
import { BaseResponseDto } from "types/api/baseResponseDto";
import { DraftFindingRequestDto } from "types/api/draftFindingRequestDto";
import { DraftFindingResponse } from "types/api/draftFindingResponse";
import { FindingFiltersRequestDto } from "types/api/findingFiltersRequestDto";
import { ListFindingResponse } from "types/api/listFindingResponse";
import {
  ReportTemplateListRequestDto,
  ReportTemplateRequestDto,
} from "types/api/reportTemplateRequestDto";
import {
  ReportTemplateListResponseDto,
  ReportTemplateResponsetDto,
} from "types/api/reportTemplateResponseDto";
import {
  AssessmentCompositeRequestDto,
  AssessmentExportData,
  AssessmentFilterValues,
  AssessmentFiltersRequestDto,
  AssessmentListingResponseDto,
  AssessmentReportRequestDto,
  AssessmentReportResponseDto,
  AssessmentResponseDto,
  AssessmentShortResponseDto,
} from "types/assessment";
import { FilterInfo } from "types/pagination";
import {
  convertDraftToPublish,
  validateDraftForPublish,
} from "utils/assessmentsUtil";

import SubProductEnvironmentTypeAPI from "./SubProductEnvironmentTypeAPI";
import ToolsIntegrationsAPI from "./ToolsIntegrationAPI";

const AssessmentAPI = {
  getAll(data: AssessmentFiltersRequestDto) {
    const modifiedData = modifyFilters(data);
    return makeCancellableAPIRequest<AssessmentListingResponseDto>(
      "POST",
      `/api/assessments/filter`,
      {
        data: modifiedData,
      },
    );
  },
  getFilterValues(params?: { productId?: number }) {
    return makeCancellableAPIRequest<AssessmentFilterValues>(
      "GET",
      `/api/assessments/filter-options`,
      {
        params,
      },
    );
  },
  createReport(data: AssessmentReportRequestDto) {
    return makeCancellableAPIRequest<AssessmentReportResponseDto>(
      "POST",
      `/api/assessments/report`,
      {
        data,
      },
    );
  },
  getReport(assessmentId: number) {
    return makeCancellableAPIRequest<AssessmentReportResponseDto>(
      "GET",
      `/api/assessments/report`,
      {
        params: { assessmentId },
      },
    );
  },
  updateReport(data: AssessmentReportRequestDto) {
    return makeCancellableAPIRequest<AssessmentReportResponseDto>(
      "PUT",
      `/api/assessments/report`,
      {
        data,
      },
    );
  },
  createAssessment(data: AssessmentCompositeRequestDto) {
    return makeCancellableAPIRequest<AssessmentResponseDto>(
      "POST",
      `/api/assessments`,
      {
        data,
      },
    );
  },
  updateAssessment(id: number, data: AssessmentCompositeRequestDto) {
    return makeCancellableAPIRequest<AssessmentResponseDto>(
      "PUT",
      `/api/assessments/${id}`,
      {
        data,
      },
    );
  },
  get(id: number | string) {
    return makeCancellableAPIRequest<AssessmentResponseDto>(
      "GET",
      `/api/assessments/${id}`,
    );
  },
  delete(id: string) {
    return makeCancellableAPIRequest("DELETE", `/api/assessments/${id}`);
  },
  listDraftFindings(assessmentId: number, payload: FindingFiltersRequestDto) {
    const omittedPayload = {
      ...payload,
      filters: payload.filters
        ? omit(payload.filters, ["assessments"])
        : payload.filters,
    };
    return makeCancellableAPIRequest<ListFindingResponse>(
      "POST",
      `/api/findings/draft/filter`,
      { data: omittedPayload, params: { assessmentId } },
    );
  },
  getDraftFinding(assessmentId: number, id: string | number) {
    return makeCancellableAPIRequest<DraftFindingResponse>(
      "GET",
      `/api/findings/draft/${id}`,
      { params: { assessmentId } },
    );
  },
  createDraftFinding(assessmentId: number, data: DraftFindingRequestDto) {
    return makeCancellableAPIRequest<DraftFindingResponse>(
      "POST",
      `/api/findings/draft`,
      {
        data,
        params: { assessmentId },
      },
    );
  },
  updateDraftFinding(
    assessmentId: number,
    id: string | number,
    data: DraftFindingRequestDto,
  ) {
    return makeCancellableAPIRequest<DraftFindingResponse>(
      "PUT",
      `/api/findings/draft/${id}`,
      {
        data,
        params: { assessmentId },
      },
    );
  },
  deleteDraftFinding(assessmentId: number, id: string | number) {
    return makeCancellableAPIRequest<void>(
      "DELETE",
      `/api/findings/draft/${id}`,
      { params: { assessmentId } },
    );
  },
  deleteAssessmentFinding(
    id: string | number,
    data: { assessmentIds: number[]; notes: string },
  ) {
    return makeCancellableAPIRequest<void>(
      "PUT",
      `user/findings/update-assessment/${id}`,
      {
        data,
      },
    );
  },
  publishFinding({
    drafts,
    productId,
    subProductId,
    assessment,
    toolName = "CUSTOM",
  }: {
    drafts: DraftFindingResponse[];
    productId: number;
    subProductId: number;
    assessment: AssessmentResponseDto;
    toolName?: string;
  }) {
    const validationPromises = [] as Promise<void>[];
    drafts.forEach((draft) => {
      validationPromises.push(validateDraftForPublish(draft));
    });
    return {
      cancel: noop,
      promise: Promise.all(validationPromises).then(async () => {
        const manualFindingPayloads = [] as DraftFindingResponse[];
        drafts.forEach((draft) => {
          const manualFindingPayload = convertDraftToPublish(
            assessment.id,
            draft,
          );
          manualFindingPayloads.push(manualFindingPayload);
        });
        const blob = new Blob([JSON.stringify(manualFindingPayloads)], {
          type: "text/plain",
        });
        let fileName;
        if (drafts.length === 1) {
          fileName = `${assessment.name}-draft-${drafts[0].id}.json`;
        } else {
          fileName = `${assessment.name}-draft-${dayjs().format("DD-MM-YYYY-HH:mm:ss")}.json`;
        }
        const fileObject = new File([blob], fileName, {
          type: "application/json",
        });
        const envs =
          await SubProductEnvironmentTypeAPI.getAllQuick(subProductId).promise;
        const _data = await ToolsIntegrationsAPI.postUploadUrl(
          {
            environment:
              find(envs, {
                name: drafts[0].environmentName,
              })?.name || drafts[0].environmentName,
            fileName: fileName,
            product: productId,
            subProduct: subProductId,
          },
          toolName,
          false,
        ).promise;
        await axios({ method: "put", url: _data.signedUrl, data: fileObject });
        const draftPromise = [] as Promise<void>[];
        drafts.forEach((draft) => {
          draftPromise.push(
            AssessmentAPI.deleteDraftFinding(assessment.id, draft.id!).promise,
          );
        });
        await Promise.all(draftPromise);
      }),
    };
  },
  fetchDraftTemplates() {
    const { cancel, promise } = makeCancellableAPIRequest<string>(
      "GET",
      "/downloads/Finding Templates v1.1.csv",
      {
        headers: {
          "Content-type": "application/text",
        },
      },
    );

    return {
      cancel,
      promise: promise.then(async (csvString) => {
        const jsonData = (await csvtojson().fromString(csvString)) as {
          [key: string]: string;
        }[];
        return jsonData;
      }),
    };
  },
  completeAndSendReport(assessmentId: number, data: { htmlString: string }) {
    return makeCancellableAPIRequest<void>(
      "POST",
      `/api/assessments/${assessmentId}/report/complete`,
      {
        data,
      },
    );
  },
  processDraftCSVFile(data: any) {
    return makeCancellableAPIRequest<{ id: number; signedUrl: string }>(
      "POST",
      `/api/findings/draft/csv`,
      {
        data,
      },
    );
  },
  checkDuplicateDraftFindingFile(params: any) {
    return makeCancellableAPIRequest<{ data: string; success: boolean }>(
      "GET",
      `api/assessments/uploads/check-duplicate`,
      {
        params,
      },
    );
  },
  processDraftPDFFile(data: any) {
    return makeCancellableAPIRequest<{ id: number; signedUrl: string }>(
      "POST",
      `/api/findings/draft/pdf`,
      {
        data,
      },
    );
  },
  uploadDraftFindingFile(id: number, checksum: string) {
    return makeCancellableAPIRequest<void>(
      "POST",
      `/api/findings/draft/files/${id}?checksum=${checksum}`,
    );
  },
  getFilteredAssessments(data: { filters: FilterInfo } | undefined) {
    return makeCancellableAPIRequest<AssessmentShortResponseDto>(
      "POST",
      "api/assessments/short/finding",
      {
        data,
      },
    );
  },
  exportCSV(data: AssessmentFiltersRequestDto) {
    const modifiedData = modifyFilters(data);
    return makeCancellableAPIRequest<BaseResponseDto<AssessmentExportData>>(
      "POST",
      `/api/assessments/export`,
      {
        data: {
          filters: modifiedData.filters,
          exportFields: data.exportFields,
          timezone: data.timezone,
        },
      },
    );
  },
  listReportTemplates(data: ReportTemplateListRequestDto) {
    return makeCancellableAPIRequest<ReportTemplateListResponseDto>(
      "POST",
      `api/assessment-report-templates/list`,
      { data },
    );
  },
  getReportTemplate(id: string | number) {
    return makeCancellableAPIRequest<ReportTemplateResponsetDto>(
      "GET",
      `api/assessment-report-templates/${id}`,
    );
  },

  createReportTemplate(data: ReportTemplateRequestDto) {
    return makeCancellableAPIRequest<ReportTemplateResponsetDto>(
      "POST",
      `api/assessment-report-templates`,
      {
        data,
      },
    );
  },
  updateReportTemplate(id: string | number, data: ReportTemplateRequestDto) {
    return makeCancellableAPIRequest<ReportTemplateResponsetDto>(
      "PUT",
      `api/assessment-report-templates/${id}`,
      {
        data,
      },
    );
  },
  deleteReportTemplate(id: string | number) {
    return makeCancellableAPIRequest<void>(
      "DELETE",
      `api/assessment-report-templates/${id}`,
    );
  },
};

export default AssessmentAPI;

const modifyFilters = (data: AssessmentFiltersRequestDto) => {
  const { filters } = data;
  if (filters.status) {
    data.filters.status = filters.status.map((i) =>
      (i as string).toUpperCase(),
    );
  }
  return data;
};
